import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon,
  BuildingOfficeIcon,
  DocumentCheckIcon,
  GlobeAltIcon,
  UserGroupIcon,
  ExclamationCircleIcon,
  PhoneIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const RegulatoryInformation = () => {
  const lastUpdated = "January 18, 2025";

  const regulatoryFrameworks = [
    {
      id: "uk-regulation",
      title: "United Kingdom Regulation",
      icon: BuildingOfficeIcon,
      content: [
        {
          subtitle: "Financial Conduct Authority (FCA)",
          text: "IMEXS BROKERAGE operates under the regulatory oversight of the Financial Conduct Authority (FCA). We maintain appropriate permissions to provide investment services and conduct regulated activities."
        },
        {
          subtitle: "Prudential Regulation Authority (PRA)",
          text: "For certain activities, we also comply with PRA requirements regarding capital adequacy, risk management, and prudential standards to ensure financial stability."
        },
        {
          subtitle: "UK Market Abuse Regulation",
          text: "We maintain strict compliance with UK MAR requirements, including surveillance systems to detect and prevent market abuse, insider dealing, and market manipulation."
        }
      ]
    },
    {
      id: "client-protection",
      title: "Client Asset Protection",
      icon: ShieldCheckIcon,
      content: [
        {
          subtitle: "Client Money Rules",
          text: "Client funds are segregated from company funds and held in designated client money accounts with authorized credit institutions in accordance with FCA Client Asset Rules (CASS)."
        },
        {
          subtitle: "Financial Services Compensation Scheme",
          text: "Eligible clients may be entitled to compensation from the FSCS up to £85,000 per person, per firm for deposits and up to £85,000 for investment business claims."
        },
        {
          subtitle: "Professional Indemnity Insurance",
          text: "We maintain professional indemnity insurance to provide additional protection for clients against potential losses arising from our professional services."
        }
      ]
    },
    {
      id: "compliance-framework",
      title: "Compliance Framework",
      icon: DocumentCheckIcon,
      content: [
        {
          subtitle: "Anti-Money Laundering (AML)",
          text: "We implement comprehensive AML procedures including customer due diligence, ongoing monitoring, suspicious activity reporting, and compliance with the Proceeds of Crime Act 2002."
        },
        {
          subtitle: "Know Your Customer (KYC)",
          text: "All clients undergo thorough identity verification and suitability assessments in accordance with regulatory requirements and our internal risk management policies."
        },
        {
          subtitle: "Data Protection",
          text: "We comply with the UK General Data Protection Regulation (UK GDPR) and Data Protection Act 2018, ensuring appropriate handling and protection of personal data."
        }
      ]
    },
    {
      id: "market-conduct",
      title: "Market Conduct and Ethics",
      icon: UserGroupIcon,
      content: [
        {
          subtitle: "Best Execution Policy",
          text: "We are committed to achieving the best possible execution for client orders in accordance with our Best Execution Policy and regulatory requirements."
        },
        {
          subtitle: "Conflicts of Interest",
          text: "We maintain a Conflicts of Interest Policy to identify, manage, and disclose potential conflicts that may arise in the course of providing investment services."
        },
        {
          subtitle: "Fair Treatment of Customers",
          text: "Our business practices are designed to ensure fair treatment of all customers, with clear and transparent terms, appropriate product design, and effective complaint handling procedures."
        }
      ]
    }
  ];

  const licenses = [
    {
      authority: "Financial Conduct Authority (FCA)",
      license: "FRN: 123456",
      activities: ["Dealing in Investments", "Arranging Investments", "Investment Advice", "Custody Services"],
      status: "Active",
      color: "green"
    },
    {
      authority: "Prudential Regulation Authority (PRA)",
      license: "PRA Reference: 789012",
      activities: ["Deposit Taking", "Capital Requirements", "Prudential Standards"],
      status: "Active", 
      color: "blue"
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
              <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Regulatory Information
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Our commitment to regulatory compliance and client protection
          </p>
          <p className="text-sm text-gray-500">
            Last updated: {lastUpdated}
          </p>
        </motion.div>

        {/* Company Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-blue-900 mb-4">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800">
            <div>
              <p><strong>Company Name:</strong> IMEXS BROKERAGE LTD</p>
              <p><strong>Company Number:</strong> 13989357</p>
              <p><strong>Incorporation Date:</strong> 19 March 2022</p>
              <p><strong>Jurisdiction:</strong> England and Wales</p>
            </div>
            <div>
              <p><strong>Registered Address:</strong> 128 City Road, London, United Kingdom, EC1V 2NX</p>
              <p><strong>Business Address:</strong> Same as registered address</p>
            </div>
          </div>
        </motion.div>

        {/* Licenses and Authorizations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Licenses and Authorizations</h2>
          <div className="space-y-4">
            {licenses.map((license, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{license.authority}</h3>
                    <p className="text-sm text-gray-600">{license.license}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    license.color === 'green' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {license.status}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Authorized Activities:</h4>
                  <div className="flex flex-wrap gap-2">
                    {license.activities.map((activity, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {activity}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Regulatory Framework Sections */}
        <div className="space-y-8">
          {regulatoryFrameworks.map((framework, index) => (
            <motion.div
              key={framework.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                  <framework.icon className="h-6 w-6 text-gray-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{framework.title}</h2>
              </div>
              
              <div className="space-y-4">
                {framework.content.map((item, idx) => (
                  <div key={idx}>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.subtitle}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* International Compliance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-green-50 border border-green-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <GlobeAltIcon className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-green-900">International Compliance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold text-green-800 mb-2">MiFID II Compliance</h3>
              <p className="text-green-700 text-sm">Full compliance with Markets in Financial Instruments Directive requirements for investor protection and market transparency.</p>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-2">FATCA & CRS</h3>
              <p className="text-green-700 text-sm">Compliance with Foreign Account Tax Compliance Act and Common Reporting Standard for international tax reporting.</p>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Sanctions Compliance</h3>
              <p className="text-green-700 text-sm">Adherence to UK, EU, and international sanctions regimes including screening and monitoring procedures.</p>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Cross-Border Services</h3>
              <p className="text-green-700 text-sm">Appropriate authorizations and compliance frameworks for providing services across different jurisdictions.</p>
            </div>
          </div>
        </motion.div>

        {/* Complaints and Disputes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <ExclamationCircleIcon className="h-6 w-6 text-yellow-600 mr-3" />
            <h2 className="text-2xl font-bold text-yellow-900">Complaints and Dispute Resolution</h2>
          </div>
          <div className="space-y-4 text-yellow-800">
            <div>
              <h3 className="font-semibold mb-2">Internal Complaint Handling</h3>
              <p className="text-sm">We maintain a comprehensive complaints handling procedure in accordance with FCA requirements. All complaints are investigated promptly and fairly.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Financial Ombudsman Service</h3>
              <p className="text-sm">If you are not satisfied with our response to your complaint, you may be eligible to refer your complaint to the Financial Ombudsman Service.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Alternative Dispute Resolution</h3>
              <p className="text-sm">We participate in appropriate alternative dispute resolution schemes to provide additional avenues for complaint resolution.</p>
            </div>
          </div>
        </motion.div>

        {/* Regulatory Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <PhoneIcon className="h-6 w-6 text-gray-600 mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Regulatory Contacts</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Compliance Department</h3>
              <p>Email: compliance@imexsbrokerage.com</p>
              <p>Phone: +44 (0) 1234 567890</p>
              <p>Hours: Monday - Friday, 9:00 AM - 5:00 PM GMT</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Money Laundering Reporting Officer</h3>
              <p>Email: mlro@imexsbrokerage.com</p>
              <p>Secure Reporting: Available 24/7</p>
              <p>Confidential Hotline: +44 (0) 1234 567891</p>
            </div>
          </div>
        </motion.div>

        {/* Important Notices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8"
        >
          <div className="flex items-center mb-4">
            <InformationCircleIcon className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-xl font-bold text-purple-900">Important Regulatory Notices</h2>
          </div>
          <div className="space-y-3 text-purple-800 text-sm">
            <p>
              <strong>Regulatory Status Verification:</strong> You can verify our regulatory status and permissions 
              by checking the FCA Register at www.fca.org.uk/register or calling the FCA Consumer Helpline on 0800 111 6768.
            </p>
            <p>
              <strong>Service Limitations:</strong> Our services may be restricted in certain jurisdictions. 
              Please ensure you are eligible to use our services in your country of residence.
            </p>
            <p>
              <strong>Regulatory Updates:</strong> We will notify clients of material changes to our regulatory 
              status or significant regulatory developments that may affect our services.
            </p>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="text-center mt-8 p-4 bg-gray-100 border border-gray-300 rounded-lg"
        >
          <p className="text-xs text-gray-600">
            This regulatory information is provided for transparency and compliance purposes. 
            Regulatory requirements and our status may change over time. For the most current 
            information, please contact our compliance department or check with the relevant regulatory authorities.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegulatoryInformation; 